import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { clientesApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Pencil, 
  Trash2,
  Users
} from 'lucide-react';

export default function ClientesPage() {
  const { empresaActual, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    empresa_cliente: '',
    cif_nif: '',
    direccion: '',
    codigo_postal: '',
    ciudad: '',
    provincia: '',
    notas: ''
  });

  const loadClientes = async () => {
    if (!empresaActual?.id) return;
    
    try {
      const response = await clientesApi.getAll(empresaActual.id);
      setClientes(response.data);
    } catch (error) {
      console.error('Error loading clientes:', error);
      toast.error('Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClientes();
  }, [empresaActual?.id]);

  const handleOpenDialog = (cliente = null) => {
    if (cliente) {
      setEditingCliente(cliente);
      setFormData({
        nombre: cliente.nombre,
        email: cliente.email,
        telefono: cliente.telefono || '',
        empresa_cliente: cliente.empresa_cliente || '',
        cif_nif: cliente.cif_nif || '',
        direccion: cliente.direccion || '',
        codigo_postal: cliente.codigo_postal || '',
        ciudad: cliente.ciudad || '',
        provincia: cliente.provincia || '',
        notas: cliente.notas || ''
      });
    } else {
      setEditingCliente(null);
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        empresa_cliente: '',
        cif_nif: '',
        direccion: '',
        codigo_postal: '',
        ciudad: '',
        provincia: '',
        notas: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCliente) {
        await clientesApi.update(editingCliente.id, formData);
        toast.success('Cliente actualizado');
      } else {
        await clientesApi.create({ ...formData, empresa_id: empresaActual.id });
        toast.success('Cliente creado');
      }
      setDialogOpen(false);
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este cliente?')) return;
    
    try {
      await clientesApi.delete(id);
      toast.success('Cliente eliminado');
      loadClientes();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const filteredClientes = clientes.filter(c => {
    const searchLower = searchTerm.toLowerCase();
    return (
      c.nombre.toLowerCase().includes(searchLower) ||
      c.email.toLowerCase().includes(searchLower) ||
      c.empresa_cliente?.toLowerCase().includes(searchLower)
    );
  });

  const canEdit = hasPermission(['OWNER', 'MANAGER']);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn" data-testid="clientes-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Clientes
            </h1>
            <p className="text-slate-500 mt-1">
              Gestiona tu base de datos de clientes
            </p>
          </div>
          {canEdit && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => handleOpenDialog()}
                  data-testid="clientes-new-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Cliente
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <form onSubmit={handleSubmit}>
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
                    </DialogTitle>
                    <DialogDescription>
                      {editingCliente ? 'Actualiza los datos del cliente' : 'Añade un nuevo cliente a tu base de datos'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="nombre">Nombre *</Label>
                        <Input
                          id="nombre"
                          value={formData.nombre}
                          onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                          required
                          data-testid="cliente-nombre-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          data-testid="cliente-email-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="telefono">Teléfono</Label>
                        <Input
                          id="telefono"
                          value={formData.telefono}
                          onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="empresa_cliente">Empresa</Label>
                        <Input
                          id="empresa_cliente"
                          value={formData.empresa_cliente}
                          onChange={(e) => setFormData({ ...formData, empresa_cliente: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cif_nif">CIF/NIF</Label>
                      <Input
                        id="cif_nif"
                        value={formData.cif_nif}
                        onChange={(e) => setFormData({ ...formData, cif_nif: e.target.value })}
                        placeholder="B12345678"
                      />
                    </div>
                    
                    {/* Dirección completa */}
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-sm font-medium text-slate-700 mb-3">Dirección</p>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label htmlFor="direccion">Calle y número</Label>
                          <Input
                            id="direccion"
                            value={formData.direccion}
                            onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                            placeholder="Calle Mayor, 123"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="codigo_postal">C.P.</Label>
                            <Input
                              id="codigo_postal"
                              value={formData.codigo_postal}
                              onChange={(e) => setFormData({ ...formData, codigo_postal: e.target.value })}
                              placeholder="28001"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="ciudad">Ciudad</Label>
                            <Input
                              id="ciudad"
                              value={formData.ciudad}
                              onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                              placeholder="Madrid"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="provincia">Provincia</Label>
                            <Input
                              id="provincia"
                              value={formData.provincia}
                              onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
                              placeholder="Madrid"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="cliente-save-btn">
                      {editingCliente ? 'Guardar Cambios' : 'Crear Cliente'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, email o empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="clientes-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : filteredClientes.length === 0 ? (
            <div className="empty-state py-16">
              <Users className="empty-state-icon" />
              <h3 className="empty-state-title">No hay clientes</h3>
              <p className="empty-state-description">
                {searchTerm 
                  ? 'No se encontraron clientes con esa búsqueda'
                  : 'Añade tu primer cliente para empezar'}
              </p>
              {canEdit && !searchTerm && (
                <Button 
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                  onClick={() => handleOpenDialog()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir Cliente
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Creado</TableHead>
                  {canEdit && <TableHead className="w-[100px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClientes.map((cliente) => (
                  <TableRow key={cliente.id} className="table-row-hover" data-testid={`cliente-row-${cliente.id}`}>
                    <TableCell className="font-medium">{cliente.nombre}</TableCell>
                    <TableCell>{cliente.empresa_cliente || '-'}</TableCell>
                    <TableCell>{cliente.email}</TableCell>
                    <TableCell>{cliente.telefono || '-'}</TableCell>
                    <TableCell>{formatDate(cliente.created_at)}</TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleOpenDialog(cliente)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(cliente.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
